import { DAYS_PER_MONTH, ageInDays, formatAge, parseDate } from '../growth/growthIndicators';

/**
 * Marcos motores do bebê: catálogo, idade corrigida e situação de cada marco
 * frente às janelas de conquista da OMS.
 *
 * Janelas em dias (percentis 1, 3, 50, 97 e 99) da tabela oficial do WHO
 * Motor Development Study (Acta Paediatrica Suppl. 2006;450:86-95):
 * cdn.who.int/media/docs/default-source/child-growth/child-growth-standards/
 * indicators/motor-development-milestones/mm_percentiles_table.pdf
 *
 * "Sustenta a cabeça" e "Rola" não fazem parte do estudo da OMS: são só
 * registrados, sem janela de referência. Os critérios são textos próprios.
 * O catálogo de chaves é o mesmo de MotorMilestones::KEYS no backend.
 */
export const MOTOR_MILESTONES = [
  {
    key: 'head_control',
    label: 'Sustenta a cabeça',
    hint: 'Mantém a cabeça firme e alinhada ao ser segurado sentado ou ao ficar de bruços.',
    window: null,
  },
  {
    key: 'rolling',
    label: 'Rola',
    hint: 'Vira sozinho de barriga para cima para de bruços, ou o contrário.',
    window: null,
  },
  {
    key: 'sitting',
    label: 'Senta sem apoio',
    hint: 'Fica sentado com a cabeça ereta por ao menos 10 segundos, sem usar mãos ou braços para se equilibrar.',
    window: { p1: 115, p3: 125, p50: 179, p97: 256, p99: 279 },
  },
  {
    key: 'standing_assisted',
    label: 'Fica em pé com apoio',
    hint: 'Fica em pé segurando um móvel firme com as mãos, sem encostar o corpo, por ao menos 10 segundos.',
    window: { p1: 147, p3: 160, p50: 226, p97: 320, p99: 348 },
  },
  {
    key: 'crawling',
    label: 'Engatinha',
    hint: 'Avança ou recua sobre mãos e joelhos, com a barriga fora do chão, em movimentos seguidos.',
    window: { p1: 157, p3: 177, p50: 254, p97: 364, p99: 409 },
    // No estudo da OMS 4,3% das crianças não engatinharam: atraso aqui não é alerta
    optional: true,
  },
  {
    key: 'walking_assisted',
    label: 'Anda com apoio',
    hint: 'Dá passos de lado ou para a frente segurando um móvel firme, com o corpo ereto.',
    window: { p1: 181, p3: 200, p50: 275, p97: 378, p99: 418 },
  },
  {
    key: 'standing_alone',
    label: 'Fica em pé sozinho',
    hint: 'Fica em pé com o corpo ereto, sem tocar em pessoas ou objetos, por ao menos 10 segundos.',
    window: { p1: 211, p3: 235, p50: 330, p97: 461, p99: 514 },
  },
  {
    key: 'walking_alone',
    label: 'Anda sozinho',
    hint: 'Dá ao menos cinco passos seguidos sem apoio, com o corpo ereto.',
    window: { p1: 250, p3: 274, p50: 365, p97: 487, p99: 534 },
  },
];

export const getMilestone = (key) => MOTOR_MILESTONES.find((milestone) => milestone.key === key);

export const WHO_MOTOR_ATTRIBUTION =
  'Janelas de conquista: OMS, WHO Motor Development Study (2006), percentis 1 a 99. Sustentar a cabeça e rolar não têm janela da OMS.';

/* ---------- idade gestacional e idade corrigida ---------- */

const TERM_DAYS = 40 * 7;
const PRETERM_WEEKS = 37;
// A correção da prematuridade vale até os 2 anos de idade cronológica
const CORRECTION_LIMIT_DAYS = 24 * DAYS_PER_MONTH;

export const gestationalLabel = (birthInfo) => {
  if (!birthInfo || birthInfo.gestationalWeeks == null) return null;
  const days = Number(birthInfo.gestationalDays) || 0;
  return days > 0 ? `${birthInfo.gestationalWeeks} semanas e ${days} ${days === 1 ? 'dia' : 'dias'}` : `${birthInfo.gestationalWeeks} semanas`;
};

/** Dias a descontar da idade de um prematuro (< 37 semanas); 0 para os demais. */
export const prematurityDays = (birthInfo) => {
  if (!birthInfo || birthInfo.gestationalWeeks == null) return 0;
  const weeks = Number(birthInfo.gestationalWeeks);
  if (!(weeks < PRETERM_WEEKS)) return 0;
  return TERM_DAYS - (weeks * 7 + (Number(birthInfo.gestationalDays) || 0));
};

/**
 * Idade numa data: `chronological` (dias desde o nascimento), `corrected`
 * (só para prematuro com menos de 2 anos, senão null) e `days`, a usada na
 * comparação com as janelas. Sem nascimento válido, null.
 */
export const ageAt = (birthday, date, birthInfo) => {
  const chronological = ageInDays(birthday, date);
  if (chronological == null || chronological < 0) return null;
  const discount = prematurityDays(birthInfo);
  const corrected = discount > 0 && chronological < CORRECTION_LIMIT_DAYS ? Math.max(0, chronological - discount) : null;
  return { chronological, corrected, days: corrected != null ? corrected : chronological };
};

export const ageLabel = (age) => {
  if (!age) return '-';
  return age.corrected != null ? `${formatAge(age.corrected)} (corrigida)` : formatAge(age.chronological);
};

export const toMonths = (days) => days / DAYS_PER_MONTH;

/* ---------- situação de cada marco ---------- */

// tone: ok (verde), info (azul), alert (laranja), neutral (cinza)
const achievedStatus = (milestone, days) => {
  const { window } = milestone;
  if (!window || days == null) return { tone: 'ok', label: 'Conquistado' };
  if (days < window.p1) return { tone: 'info', label: 'Conquistado antes da janela' };
  if (days <= window.p99) return { tone: 'ok', label: 'Conquistado dentro da janela' };
  return milestone.optional
    ? { tone: 'info', label: 'Conquistado depois da janela' }
    : { tone: 'alert', label: 'Conquistado depois da janela' };
};

const pendingStatus = (milestone, days) => {
  const { window } = milestone;
  if (!window || days == null) return { tone: 'neutral', label: 'Não registrado' };
  if (days < window.p1) return { tone: 'neutral', label: 'Ainda não esperado' };
  if (days <= window.p99) return { tone: 'info', label: 'Na janela de conquista' };
  return milestone.optional
    ? { tone: 'info', label: 'Além da janela (nem toda criança engatinha)' }
    : { tone: 'alert', label: 'Além da janela: avaliar' };
};

/**
 * Uma linha por marco do catálogo: o registro (ou null), a idade na
 * conquista e a situação. Pendentes são avaliados pela idade em `today`.
 * `records`: patient_motor_milestones do paciente.
 */
export const milestoneRows = (records, birthday, birthInfo, today) => {
  const list = Array.isArray(records) ? records : [];
  const current = ageAt(birthday, today, birthInfo);
  return MOTOR_MILESTONES.map((milestone) => {
    const record = list.find((item) => item.milestoneKey === milestone.key) || null;
    if (record) {
      const age = ageAt(birthday, record.achievedAt, birthInfo);
      return { milestone, record, age, status: achievedStatus(milestone, age && age.days) };
    }
    return { milestone, record: null, age: null, status: pendingStatus(milestone, current && current.days) };
  });
};

/** Hoje em 'YYYY-MM-DD' (data local). */
export const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const hasValidBirthday = (birthday) => parseDate(birthday) != null;
